// Jun 2026 (Randy, 3-lane listen) — escalating CTAs for the "Pay for this
// myself" listen lane, shown while the visitor stays in the public Q&A:
//   • "out-of-dots" — a signed-up FAN ran out of dots. Two-path chooser:
//     Become a creator (Stripe platform checkout) vs. Subscribe to this silo
//     (owner-pays unlimited mode, no dots needed).
//   • "become-creator" — a signed-up FAN tapped "Pay for this myself" but isn't
//     a creator yet. Explain why creators can listen on their own key/voucher,
//     and hand them to the platform-fee Stripe checkout.
//   • "setup-funding" — a CREATOR with no OpenAI key AND no funded voucher.
//     Explain a voucher (3× platform markup, disperses to all silos) vs. their
//     own OpenAI key (1×, cheapest), then "Do it now" → Dashboard → Operations.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkle, Key, Coins, Heart, SpeakerSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import AutoRenewDisclosure from "@/components/AutoRenewDisclosure";

export default function VoiceLaneCTA({ open, variant, onClose, onSubscribe, onTip, onFundVoice, siloName, signedIn = true }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Oct 3 2026 (Randy) — this disclosure said "$10/month" above a checkout
  // that charges whatever PricingLab says (it has been $15 since Jul 30). An
  // auto-renew disclosure with the wrong recurring price is the one string on
  // the site that must never be hardcoded, so it now reads the live value and
  // says nothing numeric until it has it.
  const pricing = usePublicPricing();
  const platformPriceLabel = pricing.platform_fee_cents
    ? `${fmt$(pricing.platform_fee_cents)}/month`
    : "the current platform subscription price";
  if (!open) return null;

  const becomeCreator = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/platform/checkout", { origin_url: window.location.origin, return_path: window.location.pathname });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout — please try again.");
      setBusy(false);
    }
  };

  const isCreatorVariant = variant === "become-creator";
  const isOutOfDots = variant === "out-of-dots";
  const isVoiceOff = variant === "creator-voice-off";

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="voice-lane-cta"
    >
      <div
        className="modal-safe bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 pb-0">
          <div className="w-10 h-10 rounded-full bg-[#E8EBE5] flex items-center justify-center">
            {isVoiceOff
              ? <SpeakerSlash size={20} weight="duotone" className="text-[#4A5D4E]" />
              : isOutOfDots
                ? <Coins size={20} weight="duotone" className="text-[#4A5D4E]" />
                : isCreatorVariant
                  ? <Sparkle size={20} weight="duotone" className="text-[#4A5D4E]" />
                  : <Key size={20} weight="duotone" className="text-[#4A5D4E]" />}
          </div>
          <button
            onClick={onClose}
            className="text-[#6B6A68] hover:text-[#1C1B1A] transition-colors"
            data-testid="voice-lane-cta-close"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-5 pt-3">
          {isVoiceOff ? (
            <>
              <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
                Premium voice isn't switched on yet.
              </h3>
              <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                {siloName ? <strong>{siloName}</strong> : "This creator"} hasn't set up
                premium AI voice for new answers yet. Already-voiced (green) replies still
                replay free — or you can chip in to switch premium voice on for everyone.
              </p>
              {onFundVoice && (
                <button
                  onClick={() => { onClose?.(); onFundVoice?.(); }}
                  data-testid="voice-off-fund"
                  className="mt-5 w-full py-3 rounded-full bg-[#4A5D4E] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors flex items-center justify-center gap-2"
                >
                  <Heart size={16} weight="fill" /> Chip in to switch on voice
                </button>
              )}
              <button
                onClick={onClose}
                data-testid="voice-off-dismiss"
                className="mt-2 w-full py-2.5 rounded-full bg-white border border-[#E2E0DA] text-[#1C1B1A] text-sm font-semibold hover:bg-[#F0F1EE] transition-colors"
              >
                Got it
              </button>
            </>
          ) : isOutOfDots ? (
            <>
              <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
                You're out of dots.
              </h3>
              <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                Dots are how new fans ask questions and/or use premium voice to
                preview the Silo. You've used yours up — here {signedIn ? "are three" : "are two"} ways
                to keep going:
              </p>

              <div className="mt-4 space-y-3">
                {/* Path A — Tip to reset your board (fastest, immediate) */}
                <div className="rounded-xl border border-[#E2E0DA] p-4" data-testid="voice-lane-path-tip">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#FDF1ED] flex items-center justify-center shrink-0">
                      <Coins size={16} weight="duotone" className="text-[#C16E5A]" />
                    </div>
                    <div className="text-sm font-semibold text-[#1C1B1A]">Tip to reset your board</div>
                  </div>
                  <p className="text-[12.5px] text-[#6B6A68] mt-2 leading-relaxed">
                    Send the creator a tip and your dot board resets — you'll have
                    fresh dots to keep asking and listening right away.
                  </p>
                  <button
                    onClick={() => { onClose?.(); onTip?.(); }}
                    data-testid="voice-lane-tip"
                    className="mt-3 w-full py-2.5 rounded-full bg-[#C16E5A] text-white text-sm font-semibold hover:bg-[#a85946] transition-colors flex items-center justify-center gap-2"
                  >
                    <Coins size={16} weight="fill" /> Tip to reset your board
                  </button>
                </div>

                {/* Path B — Become a creator (signed-in fans only) */}
                {signedIn && (
                  <div className="rounded-xl border border-[#E2E0DA] p-4" data-testid="voice-lane-path-creator">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#E8EBE5] flex items-center justify-center shrink-0">
                        <Sparkle size={16} weight="duotone" className="text-[#4A5D4E]" />
                      </div>
                      <div className="text-sm font-semibold text-[#1C1B1A]">Become a creator</div>
                    </div>
                    <p className="text-[12.5px] text-[#6B6A68] mt-2 leading-relaxed">
                      Grants a few more dots — but becoming a creator is really a
                      larger choice! You see value in creating your own Silo(s).
                      We'll send you to Account &amp; Billing for setup.
                    </p>
                    <button
                      onClick={becomeCreator}
                      disabled={busy}
                      data-testid="voice-lane-become-creator"
                      className="mt-3 w-full py-2.5 rounded-full bg-[#1C1B1A] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors disabled:opacity-60"
                    >
                      {busy ? "Opening checkout…" : "Become a creator"}
                    </button>
                    <AutoRenewDisclosure priceLabel={platformPriceLabel} dense className="mt-2" />
                    <p className="text-[11px] text-[#9A9892] mt-2 leading-relaxed">
                      Heads up: this leaves the chat. But when you come back, simply
                      use the <strong>Recall</strong> button up top to reload your convo.
                    </p>
                  </div>
                )}

                {/* Path C — Subscribe to this silo */}
                <div className="rounded-xl border border-[#E2E0DA] p-4" data-testid="voice-lane-path-subscribe">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#F3EAE6] flex items-center justify-center shrink-0">
                      <Heart size={16} weight="duotone" className="text-[#C16E5A]" />
                    </div>
                    <div className="text-sm font-semibold text-[#1C1B1A]">Subscribe to this silo</div>
                  </div>
                  <p className="text-[12.5px] text-[#6B6A68] mt-2 leading-relaxed">
                    Switch to unlimited mode (within a generous allowance) — the owner
                    covers it, so you won't need dots here at all.
                  </p>
                  <button
                    onClick={() => { onClose?.(); onSubscribe?.(); }}
                    data-testid="voice-lane-subscribe"
                    className="mt-3 w-full py-2.5 rounded-full bg-white border border-[#1C1B1A] text-[#1C1B1A] text-sm font-semibold hover:bg-[#F0F1EE] transition-colors"
                  >
                    Subscribe to this silo
                  </button>
                </div>
              </div>
            </>
          ) : isCreatorVariant ? (
            <>
              <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
                Listen in premium — on your own key.
              </h3>
              <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                "Pay for this myself" lets you hear any silo in premium AI voice using{" "}
                <strong>your own</strong> OpenAI key or voucher. That's a creator perk —
                become a creator and you'll get your own dashboard, your own silos, and
                pay-as-you-go premium voice anywhere on My&nbsp;Silo.
              </p>
              <button
                onClick={becomeCreator}
                disabled={busy}
                data-testid="voice-lane-become-creator"
                className="mt-5 w-full py-3 rounded-full bg-[#1C1B1A] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors disabled:opacity-60"
              >
                {busy ? "Opening checkout…" : "Become a creator"}
              </button>
              <AutoRenewDisclosure priceLabel={platformPriceLabel} dense className="mt-3" />
              <p className="text-[11px] text-[#9A9892] mt-2 text-center">
                You'll come right back here when you're done.
              </p>
            </>
          ) : (
            <>
              <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
                Turn on your premium voice.
              </h3>
              <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
                To listen on your own dime, set up one of these:
              </p>
              <div className="mt-3 space-y-2">
                <div className="flex gap-2.5 items-start bg-[#F0F1EE] rounded-lg p-3">
                  <Key size={18} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-[#1C1B1A]">
                      Your own OpenAI key <span className="text-[#2F6B3A]">(cheapest · 1×)</span>
                    </div>
                    <div className="text-[12px] text-[#6B6A68]">
                      Bills your own OpenAI account directly — no platform markup. Best value.
                    </div>
                  </div>
                </div>
                <div className="flex gap-2.5 items-start bg-[#F0F1EE] rounded-lg p-3">
                  <Coins size={18} weight="duotone" className="text-[#4A5D4E] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-[#1C1B1A]">
                      A funded OpenAI voucher <span className="text-[#6B6A68]">(3× platform)</span>
                    </div>
                    <div className="text-[12px] text-[#6B6A68]">
                      Top up once with a card — it disperses across all your silos automatically.
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { onClose?.(); navigate("/dashboard/account-settings?focus=vouchers"); }}
                data-testid="voice-lane-do-it-now"
                className="mt-5 w-full py-3 rounded-full bg-[#1C1B1A] text-white text-sm font-semibold hover:bg-[#3A4A3E] transition-colors"
              >
                Do it now
              </button>
              <p className="text-[11px] text-[#9A9892] mt-2 text-center">
                Opens Account &amp; Billing → Vouchers, where you can top up your OpenAI bucket.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
